/**
 * Base class for onboarding steps that show instructions with an optional
 * "help" sub-view. Concrete steps subclass this and provide:
 *
 *   id              - step id used by the router and the wrapper data-for
 *   title           - step title shown by the onboarding chrome
 *   previousStepId  - step id to return to when Back is pressed on the main view
 *   nextStepId      - step id to advance to when the primary button is pressed
 *   getContent()    - returns the copy/markup for both views
 *
 * `getContent()` returns:
 *   {
 *     mainHero:         HTML inserted inside the hero box (e.g. <img> or placeholder span). Optional.
 *     mainHeading:      Heading text for the main view.
 *     mainBody:         HTML body for the main view (paragraphs, lists, etc).
 *     primaryLabel:     Label for the main view's primary button.
 *     helpLabel:        Label for the "help me with this" link. Omit to hide the link entirely.
 *     helpHeading:      Heading text for the help view.
 *     helpBody:         HTML body for the help view.
 *     helpPrimaryLabel: Label for the help view's primary button. Falls back to `primaryLabel`.
 *   }
 */
export class StepInstructionGuide {
  constructor (onboarding) {
    this.onboarding = onboarding;
  }

  get template () {
    const c = this.getContent();
    const heroHtml = c.mainHero
      ? `<div class="onboarding-instruction__hero" aria-hidden="true">${c.mainHero}</div>`
      : '';
    const helpToggleHtml = c.helpLabel
      ? `<button type="button" class="onboarding-guide-help-btn onboarding-instruction__help-link" data-action="help-toggle">${c.helpLabel}</button>`
      : '';

    return `
      <div class="onboarding-step" data-for="${this.id}">
        <div class="onboarding-guide onboarding-instruction">

          <div class="onboarding-instruction__view" data-view="main">
            ${heroHtml}
            <h2 class="onboarding-guide-heading onboarding-instruction__title">${c.mainHeading}</h2>
            ${c.mainBody}
            <div class="onboarding-instruction__actions">
              <button class="button onboarding-guide-next-btn" data-action="primary">${c.primaryLabel}</button>
              ${helpToggleHtml}
            </div>
          </div>

          <div class="onboarding-instruction__view" data-view="help">
            <h2 class="onboarding-guide-heading onboarding-instruction__title">${c.helpHeading}</h2>
            ${c.helpBody}
            <div class="onboarding-instruction__actions">
              <button class="button onboarding-guide-next-btn" data-action="help-primary">${c.helpPrimaryLabel || c.primaryLabel}</button>
            </div>
          </div>

        </div>
      </div>
    `;
  }

  onBack = () => {
    // If the help sub-view is open, Back returns to the main view of this step.
    if (this.$helpView && this.$helpView.hasClass('is-active')) {
      this.showMainView();
      return;
    }
    if (this.previousStepId) {
      this.onboarding.goToStep(this.previousStepId);
    }
  }

  onActivate = () => {
    this.onboarding.showBackButton();
    // Always re-enter on the main view.
    this.showMainView();
  }

  showMainView = () => {
    if (!this.$mainView || !this.$helpView) return;
    this.$mainView.addClass('is-active');
    this.$helpView.removeClass('is-active');
  }

  showHelpView = () => {
    this.$mainView.removeClass('is-active');
    this.$helpView.addClass('is-active');
  }

  proceedToNext = () => {
    if (this.nextStepId) {
      this.onboarding.goToStep(this.nextStepId);
    }
  }

  bindActions = () => {
    // Scope selectors to this step so multiple instruction guides can coexist.
    const $container = $(this.onboarding.container);
    const $step = $container.find(`.onboarding-step[data-for="${this.id}"]`);

    this.$mainView       = $step.find('[data-view="main"]');
    this.$helpView       = $step.find('[data-view="help"]');
    this.$primaryBtn     = $step.find('[data-action="primary"]');
    this.$helpToggleBtn  = $step.find('[data-action="help-toggle"]');
    this.$helpPrimaryBtn = $step.find('[data-action="help-primary"]');

    this.showMainView();

    this.$primaryBtn.on('click', () => this.proceedToNext());
    this.$helpToggleBtn.on('click', () => this.showHelpView());
    this.$helpPrimaryBtn.on('click', () => this.proceedToNext());
  }
}